import { 
  GroupedAccounts, 
  SocialAccount, 
  SocialPlatform, 
  SelectedAccounts 
} from './types';
import { API_BASE_URL } from '../../config/api';

class MultiAccountService {
  
  /**
   * Get all accounts grouped by platform
   */
  async getGroupedAccounts(): Promise<GroupedAccounts> {
    try {
      console.log('Fetching accounts from:', `${API_BASE_URL}/social-media/accounts`);
      
      const response = await fetch(`${API_BASE_URL}/social-media/accounts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }
      
      const accounts: SocialAccount[] = await response.json();
      console.log('Raw accounts from API:', accounts);
      
      // Log profile image URLs specifically
      accounts.forEach(account => {
        console.log(`Account ${account.platformUsername}:`, {
          profileImageUrl: account.profileImageUrl,
          displayName: account.displayName,
          platform: account.platform
        });
      });
      
      // Group accounts by platform
      const grouped: GroupedAccounts = {
        youtube: accounts.filter(acc => acc.platform === 'youtube'),
        instagram: accounts.filter(acc => acc.platform === 'instagram'),
        tiktok: accounts.filter(acc => acc.platform === 'tiktok')
      };
      
      console.log('Grouped accounts:', grouped);
      
      return grouped;
    } catch (error) {
      console.error('Error fetching grouped accounts:', error);
      return { youtube: [], instagram: [], tiktok: [] };
    }
  }

  /**
   * Get accounts for a specific platform
   */
  async getAccountsByPlatform(platform: SocialPlatform): Promise<SocialAccount[]> {
    try {
      const grouped = await this.getGroupedAccounts();
      return grouped[platform] || [];
    } catch (error) {
      console.error(`Error fetching ${platform} accounts:`, error);
      return [];
    }
  }

  /**
   * Set default account for a platform
   */
  async setDefaultAccount(accountId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/accounts/${accountId}/set-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to set default account');
      }
    } catch (error) {
      console.error('Error setting default account:', error);
      throw error;
    }
  }

  /**
   * Update account nickname
   */
  async updateAccountNickname(accountId: string, nickname: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/accounts/${accountId}/nickname`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nickname })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update account nickname');
      }
    } catch (error) {
      console.error('Error updating account nickname:', error);
      throw error;
    }
  }

  /**
   * Manually refresh token for an expired account
   */
  async refreshAccountToken(accountId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/accounts/${accountId}/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh account token');
      }
      
      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error refreshing account token:', error);
      throw error;
    }
  }

  /**
   * Get default accounts for each platform
   */
  getDefaultAccounts(groupedAccounts: GroupedAccounts): SelectedAccounts {
    const defaults: SelectedAccounts = {};
    
    Object.entries(groupedAccounts).forEach(([platform, accounts]) => {
      // Look for explicitly marked default first
      let defaultAccount = accounts.find(account => account.isDefault === true);
      
      // If no explicit default, use the first active account
      if (!defaultAccount) {
        defaultAccount = accounts.find(account => account.isActive && !account.isExpired);
      }
      
      if (defaultAccount) {
        defaults[platform as SocialPlatform] = defaultAccount.id;
      }
    });
    
    return defaults;
  }

  /**
   * Get active (non-expired) accounts from grouped accounts
   */
  getActiveAccounts(groupedAccounts: GroupedAccounts): GroupedAccounts {
    const activeAccounts: GroupedAccounts = {
      youtube: [],
      instagram: [],
      tiktok: []
    };
    
    Object.entries(groupedAccounts).forEach(([platform, accounts]) => {
      activeAccounts[platform as SocialPlatform] = accounts.filter(
        account => account.isActive && !account.isExpired
      );
    });
    
    return activeAccounts;
  }

  /**
   * Find account by ID across all platforms
   */
  findAccountById(groupedAccounts: GroupedAccounts, accountId: string): SocialAccount | null {
    for (const accounts of Object.values(groupedAccounts)) {
      const account = accounts.find(acc => acc.id === accountId);
      if (account) return account;
    }
    return null;
  }

  /**
   * Get formatted account display name
   */
  getAccountDisplayName(account: SocialAccount): string {
    return account.accountNickname || 
           account.displayName || 
           account.platformUsername || 
           'Unnamed Account';
  }

  /**
   * Get account subtitle (subscriber count, etc.)
   */
  getAccountSubtitle(account: SocialAccount): string {
    const metadata = account.accountMetadata;
    if (!metadata) return '';
    
    if (metadata.subscriberCount) {
      return `${this.formatNumber(metadata.subscriberCount)} subscribers`;
    }
    
    if (metadata.followerCount) {
      return `${this.formatNumber(metadata.followerCount)} followers`;
    }
    
    return '';
  }

  /**
   * Format numbers for display (1.2K, 1.5M, etc.)
   */
  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  /**
   * Save selected accounts to localStorage for persistence
   */
  saveSelectedAccounts(selectedAccounts: SelectedAccounts): void {
    localStorage.setItem('selectedSocialAccounts', JSON.stringify(selectedAccounts));
  }

  /**
   * Load selected accounts from localStorage
   */
  loadSelectedAccounts(): SelectedAccounts {
    try {
      const saved = localStorage.getItem('selectedSocialAccounts');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }
}

export const multiAccountService = new MultiAccountService();
